import { type MapLibreMap, Marker } from 'maplibre-gl';
import type { MapLocation } from '@repo/shared';
import styles from './map-markers.module.css';

/**
 * The pins on the map.
 *
 * Two things get marked, and they are not the same thing: a place you asked
 * for, and where your device thinks you are. A search result is a claim about a
 * name; a geolocation fix is a measurement with error bars. They get different
 * marks — a pin with the label you searched for, and the conventional pulsing
 * blue dot — so nobody reads one as the other.
 *
 * Saved places get a third, quieter mark, and are clickable: seeing your places
 * on the map is most of the value of having saved them.
 *
 * `anchor: 'bottom'` matters on the pins. MapLibre's default is `'center'`,
 * which floats a teardrop's tip above the coordinate it points at — an error
 * nobody notices until they compare it against a landmark they know.
 */

export type MarkerKind = 'search' | 'located';

/** The pin element, as markup. `currentColor` carries the accent through. */
function pinElement(kind: MarkerKind, label: string, animate: boolean): HTMLElement {
  const element = document.createElement('div');
  element.className = styles.marker as string;
  element.dataset.animate = String(animate);
  element.title = label;

  element.innerHTML = `
    <svg class="${styles.pin}" viewBox="0 0 26 34" aria-hidden="true">
      <path
        d="M13 33.2C13 33.2 24 21.4 24 13A11 11 0 1 0 2 13c0 8.4 11 20.2 11 20.2Z"
        fill="${kind === 'search' ? 'var(--color-accent)' : 'var(--color-success)'}"
        stroke="rgba(4, 10, 16, 0.85)"
        stroke-width="1.5"
      />
      <circle cx="13" cy="13" r="4" fill="rgba(4, 10, 16, 0.85)" />
    </svg>
  `;

  const chip = document.createElement('span');
  chip.className = styles.label as string;
  // textContent, not innerHTML: the label comes from a geocoding response.
  chip.textContent = label;
  element.append(chip);

  return element;
}

/** The pulsing dot for a device fix. Centred, because a fix has no tip. */
function dotElement(label: string): HTMLElement {
  const element = document.createElement('div');
  element.className = styles.dotMarker as string;
  element.title = label;

  const dot = document.createElement('span');
  dot.className = styles.dot as string;
  element.append(dot);

  return element;
}

export interface TargetMarkerOptions {
  kind: MarkerKind;
  /** Plays the drop-in animation. Off when only the label changed. */
  animate?: boolean;
}

/**
 * Builds and adds the marker for the currently-targeted place.
 *
 * The caller owns the returned marker and is responsible for removing it — one
 * target is on the map at a time, and swapping it is a remove-then-add.
 */
export function addTargetMarker(
  map: MapLibreMap,
  location: MapLocation,
  options: TargetMarkerOptions,
): Marker {
  const { kind, animate = true } = options;
  const element =
    kind === 'located' ? dotElement(location.label) : pinElement(kind, location.label, animate);

  return new Marker({
    element,
    // A teardrop points with its tip; a dot points with its middle.
    anchor: kind === 'located' ? 'center' : 'bottom',
  })
    .setLngLat([location.longitude, location.latitude])
    .addTo(map);
}

/**
 * Mirrors the saved-places list onto the map.
 *
 * Markers are keyed by id and diffed, so panning around with a dozen saved
 * places doesn't churn the DOM. Called from an effect on every list change.
 */
export function syncSavedMarkers(
  map: MapLibreMap,
  locations: readonly (MapLocation & { id: string })[],
  onSelect: (location: MapLocation & { id: string }) => void,
): void {
  const existing = savedMarkers.get(map) ?? new Map<string, Marker>();
  const wanted = new Set(locations.map((location) => location.id));

  for (const [id, marker] of existing) {
    if (wanted.has(id)) continue;
    marker.remove();
    existing.delete(id);
  }

  for (const location of locations) {
    if (existing.has(location.id)) continue;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = styles.savedMarker as string;
    button.title = location.label;
    button.setAttribute('aria-label', `Go to ${location.label}`);
    button.addEventListener('click', () => onSelect(location));

    existing.set(
      location.id,
      new Marker({ element: button, anchor: 'center' })
        .setLngLat([location.longitude, location.latitude])
        .addTo(map),
    );
  }

  savedMarkers.set(map, existing);
}

/** Removes every saved-place marker, e.g. when the map is torn down. */
export function removeSavedMarkers(map: MapLibreMap): void {
  const existing = savedMarkers.get(map);
  if (!existing) return;

  for (const marker of existing.values()) marker.remove();
  savedMarkers.delete(map);
}

/**
 * Which saved markers are on which map. A WeakMap so a torn-down map takes its
 * markers with it, the same bookkeeping pattern the radar layers use.
 */
const savedMarkers = new WeakMap<MapLibreMap, Map<string, Marker>>();
